import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import {
  getScreensaverFiles,
  uploadScreensaverFile,
  deleteScreensaverFile,
  updateScreensaverFile,
  updateFilesOrder,
  getScreensaverSettings,
  saveScreensaverSettings
} from '../../../../actions/infoKiosk';
import CustomCheckbox from '../../../../assets/CustomCheckbox/CustomCheckbox';
import Confirmation from '../../../../assets/Confirmation/Confirmation';
import Loader from 'react-loader';
import styles from './styles.module.scss';

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'mp4', 'webm', 'mov'];
const ALLOWED_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/webp',
  'video/mp4', 'video/webm', 'video/quicktime'
];

const ScreensaverTab = ({
  files,
  filesLoading,
  fileUploading,
  settings,
  settingsLoading,
  updateRight,
  getScreensaverFiles,
  uploadScreensaverFile,
  deleteScreensaverFile,
  updateScreensaverFile,
  updateFilesOrder,
  getScreensaverSettings,
  saveScreensaverSettings
}) => {
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [localSettings, setLocalSettings] = useState({ timeout_sec: 10, sleep_after_sec: 120 });
  const [settingsErrors, setSettingsErrors] = useState({});

  useEffect(() => {
    getScreensaverFiles();
    getScreensaverSettings();
  }, [getScreensaverFiles, getScreensaverSettings]);

  useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      alert(`Недопустимый формат файла. Разрешены: ${ALLOWED_EXTENSIONS.join(', ')}`);
      e.target.value = '';
      return;
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      alert('Недопустимый тип файла');
      e.target.value = '';
      return;
    }

    uploadScreensaverFile(file);
    e.target.value = '';
  };

  const handleDelete = (fileId) => {
    deleteScreensaverFile(fileId);
    setDeleteConfirm(null);
  };

  const handleActiveToggle = (fileId, isActive) => {
    updateScreensaverFile(fileId, { is_active: isActive });
  };

  const moveFile = (index, direction) => {
    const activeFiles = files.filter(f => f.is_active);
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= activeFiles.length) return;

    const newFiles = [...activeFiles];
    [newFiles[index], newFiles[newIndex]] = [newFiles[newIndex], newFiles[index]];

    const orderData = newFiles.map((f, idx) => ({
      id: f.id,
      display_order: idx + 1
    }));
    updateFilesOrder(orderData);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ru-RU');
  };

  const getFileTypeLabel = (mimeType) => {
    if (mimeType?.startsWith('image/')) return 'Изображение';
    if (mimeType?.startsWith('video/')) return 'Видео';
    return mimeType || 'Неизвестно';
  };

  const validateSettings = () => {
    const errors = {};

    if (!localSettings.timeout_sec || localSettings.timeout_sec < 5) {
      errors.timeout_sec = 'Минимум 5 секунд';
    }

    if (!localSettings.sleep_after_sec || localSettings.sleep_after_sec < 10) {
      errors.sleep_after_sec = 'Минимум 10 секунд';
    }

    setSettingsErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSettingsChange = (field, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [field]: parseInt(value, 10) || ''
    }));
    if (settingsErrors[field]) {
      setSettingsErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSaveSettings = () => {
    if (!validateSettings()) return;
    saveScreensaverSettings(localSettings);
  };

  const isLoading = (filesLoading || settingsLoading) && !files.length;

  if (isLoading) {
    return <div className={styles.loader}><Loader color="#7b2b2a" loaded={false} /></div>;
  }

  // Separate active and inactive files
  const activeFiles = files.filter(f => f.is_active);
  const inactiveFiles = files.filter(f => !f.is_active);
  const sortedFiles = [...activeFiles, ...inactiveFiles];

  return (
    <div className={styles.screensaverTab}>
      {/* Files Section */}
      <section className={styles.filesSection}>
        <h3>Файлы заставки</h3>

        {updateRight && (
          <div className={styles.uploadSection}>
            <label htmlFor="screensaverFileInput">
              {fileUploading ? 'Загрузка...' : 'Загрузить файл'}
            </label>
            <input
              id="screensaverFileInput"
              type="file"
              accept={ALLOWED_MIME_TYPES.join(',')}
              onChange={handleFileSelect}
              disabled={fileUploading}
              hidden
            />
            <span className={styles.hint}>
              Форматы: {ALLOWED_EXTENSIONS.join(', ')}
            </span>
          </div>
        )}

        {sortedFiles.length > 0 ? (
          <table className={styles.filesTable}>
            <thead>
              <tr>
                <th>Имя файла</th>
                <th style={{ width: 120 }}>Тип</th>
                <th style={{ width: 100 }}>Размер</th>
                <th style={{ width: 80 }}>Активен</th>
                <th style={{ width: 160 }}>Загружен</th>
                <th style={{ width: 140 }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {sortedFiles.map((file, index) => {
                const activeIndex = file.is_active ? activeFiles.findIndex(f => f.id === file.id) : -1;
                return (
                  <tr key={file.id}>
                    <td className={styles.fileNameCell} title={file.file_name}>{file.file_name}</td>
                    <td>
                      <span className={styles.fileType}>{getFileTypeLabel(file.mime_type)}</span>
                    </td>
                    <td>{formatFileSize(file.file_size)}</td>
                    <td>
                      <CustomCheckbox
                        checked={file.is_active}
                        onChange={(e) => handleActiveToggle(file.id, e.target.checked)}
                        disabled={!updateRight}
                      />
                    </td>
                    <td>{formatDate(file.uploaded_at)}</td>
                    <td>
                      <div className={styles.rowActions}>
                        {file.is_active && (
                          <>
                            <button
                              type="button"
                              onClick={() => moveFile(activeIndex, -1)}
                              disabled={activeIndex === 0 || !updateRight}
                              title="Вверх"
                            >
                              <FaArrowUp />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveFile(activeIndex, 1)}
                              disabled={activeIndex === activeFiles.length - 1 || !updateRight}
                              title="Вниз"
                            >
                              <FaArrowDown />
                            </button>
                          </>
                        )}
                        {updateRight && (
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(file)}
                            className={styles.deleteBtn}
                            title="Удалить"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className={styles.emptyMessage}>Нет загруженных файлов</p>
        )}
      </section>

      {/* Settings Section */}
      <section className={styles.settingsSection}>
        <h3>Настройки заставки</h3>

        <div className={styles.settingsForm}>
          <div className={`${styles.formGroup} ${settingsErrors.timeout_sec ? styles.inputError : ''}`}>
            <label htmlFor="timeout_sec">Время показа слайда (секунды)</label>
            <input
              id="timeout_sec"
              type="number"
              min={5}
              value={localSettings.timeout_sec}
              onChange={(e) => handleSettingsChange('timeout_sec', e.target.value)}
              disabled={!updateRight}
            />
            {settingsErrors.timeout_sec && (
              <div className={styles.errorText}>{settingsErrors.timeout_sec}</div>
            )}
            <div className={styles.hint}>Минимум 5 секунд</div>
          </div>

          <div className={`${styles.formGroup} ${settingsErrors.sleep_after_sec ? styles.inputError : ''}`}>
            <label htmlFor="sleep_after_sec">Время до засыпания (секунды)</label>
            <input
              id="sleep_after_sec"
              type="number"
              min={10}
              value={localSettings.sleep_after_sec}
              onChange={(e) => handleSettingsChange('sleep_after_sec', e.target.value)}
              disabled={!updateRight}
            />
            {settingsErrors.sleep_after_sec && (
              <div className={styles.errorText}>{settingsErrors.sleep_after_sec}</div>
            )}
            <div className={styles.hint}>Минимум 10 секунд</div>
          </div>

          {updateRight && (
            <div className={styles.settingsActions}>
              <button
                onClick={handleSaveSettings}
                disabled={settingsLoading}
              >
                {settingsLoading ? 'Сохранение...' : 'Сохранить настройки'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Confirmation
          title={`Удалить файл "${deleteConfirm.file_name}"?`}
          acceptConfirmation={() => handleDelete(deleteConfirm.id)}
          rejectConfirmation={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ infoKiosk }) => ({
  files: infoKiosk.files,
  filesLoading: infoKiosk.filesLoading,
  fileUploading: infoKiosk.fileUploading,
  settings: infoKiosk.settings,
  settingsLoading: infoKiosk.settingsLoading
});

export default connect(mapStateToProps, {
  getScreensaverFiles,
  uploadScreensaverFile,
  deleteScreensaverFile,
  updateScreensaverFile,
  updateFilesOrder,
  getScreensaverSettings,
  saveScreensaverSettings
})(ScreensaverTab);
